import { runInAction, action } from 'mobx'
import assert from 'assert'
import memoize from 'memoizee'
import { Store } from 'libx'
import Message from 'domain/Message'
import Conversation from 'domain/Conversation'
import layer from 'layer-websdk'
import moment from 'moment'
import path from 'path'
import layerUtil from 'utils/layerUtil'
import { makeMomentSorter } from 'utils/arrayUtil'
import { createNotifier } from 'misc/notifier'
import env from 'misc/env'
import ifvisible from 'ifvisible.js'

const sortByDate = makeMomentSorter('asc', x => {
  return x ? x.sentAt : moment()
})

export default class MessageStore extends Store {
  constructor () {
    super(...arguments)

    // This store is reused across multiple projects, so we assert
    // that the injected stores have the methods we need.
    assert(this.rootStore, 'no rootStore passed to MessageStore')
    assert(this.rootStore.userStore, 'rootStore needs a userStore')
    assert(this.rootStore.userStore.refreshProfile, 'userStore needs a refreshProfile(auth0Id) method')
    assert(this.rootStore.userStore.userByAuthId, 'userStore needs a userByAuthId(auth0Id) method')
    assert(this.rootStore.authStore, 'rootStore needs an authStore')
    assert(this.rootStore.authStore.onLogout, 'authStore needs an onLogout(fn) method')

    this.markAsReadQueue = []
    this.onIncomingMessage = createNotifier()
    this.messages = this.collection({ model: Message })
    this.conversations = this.collection({ model: Conversation })
    this.fetchMessagesForConversation = memoize(this.fetchMessagesForConversation, { promise: true })
    this.getLayerClient = layerUtil({
      authStore: this.rootStore.authStore,
      auth0ClientId: env.AUTH0_CLIENT_ID,
      appId: env.LAYER_APP_ID,
      delegationEndpoint: 'https://' + path.join(env.AUTH0_DOMAIN, '/delegation')
    })
    this.rootStore.authStore
      .onLogout(action(() => {
        this.messages.clear()
        this.conversations.clear()
      }))
      .onLogin(action(() => {
        this.setupNotifications()
      }))

    ifvisible.on('focus', () => this.drainMarkAsReadQueue())
    ifvisible.on('wakeup', () => this.drainMarkAsReadQueue())
  }

  drainMarkAsReadQueue () {
    if (this.markAsReadQueue.length === 0) {
      return
    }

    this.markAsReadQueue.forEach(message => {
      this.markAsRead(message)
    })
    this.markAsReadQueue = []
  }

  async setupNotifications () {
    const client = await this.getLayerClient()
    client.on('messages:notify', async (evt) => {
      const layerMessage = evt.message
      if (layerMessage instanceof layer.Message === false) {
        return
      }

      await this._addMessages([layerMessage])
      const message = this.messages.get(layerMessage.id)
      this.onIncomingMessage.notify(message)
    })
  }

  messagesIn (conversationId) {
    const messages = this.messages.filter(x => x.conversationId === conversationId)
    return messages.sort(sortByDate)
  }

  /**
   * Gets the unread message count in the conversation.
   * @param conversationId
   */
  unreadIn (conversationId) {
    return this.messages.filter(x => x.conversationId === conversationId && x.isRead === false).length
  }

  @action loadMore (conversationId) {
    const convo = this.conversations.get(conversationId)
    convo.loadingMore = true
    convo.query.update({
      paginationWindow: convo.paginationWindow += 30
    })
  }

  async _addMessages (data) {
    // Adds messages to the store but makes sure
    // all related entities are present first.
    // Fetch all users
    const userIds = data.map(x => x.sender.userId).filter(x => x)
    await Promise.all(
      userIds.map(id => this.rootStore.userStore.refreshProfile(id))
    )

    runInAction(() => {
      this.messages.set(data)
    })
  }

  async fetchMessagesForConversation (conversationId) {
    const client = await this.getLayerClient()
    let convo = this.conversations.get(conversationId)
    if (convo && convo.query) {
      return this.messagesIn(conversationId)
    }

    convo = runInAction(() => this.conversations.set({
      id: conversationId,
      query: client.createQuery({
        model: layer.Query.Message,
        predicate: `conversation.id = "${conversationId}"`
      })
    }))

    const query = convo.query
    return new Promise((resolve) => {
      // This will resolve the promise the first time, and then
      // update the messages with changes.
      query.on('change', async (evt) => {
        if (evt.type === 'data') {
          runInAction(() => {
            convo.loadingMore = false
          })
        }

        const data = query.data
        await this._addMessages(data)

        runInAction(() => {
          convo.hasMoreMessages = !query.pagedToEnd
        })

        resolve()
      })
    })
  }

  async sendMessage ({ conversationId, text }) {
    const client = await this.getLayerClient()
    const convo = client.getConversation(conversationId, true)
    const message = convo.createMessage(text)
    runInAction(() => {
      this.messages.set(message, { createdHere: true })
    })
    message.send({
      title: `Message from ${this.rootStore.authStore.user.displayName}`,
      text
    })
    this.rootStore.soundStore.playSendMessage()
  }

  async markAsRead (message) {
    if (ifvisible.now('hidden') || ifvisible.now('idle')) {
      this.markAsReadQueue.push(message)
      return
    }

    const client = await this.getLayerClient()
    // This triggers Layer to update the read status.
    const messageInstance = client.getMessage(message.id)
    messageInstance.isRead = true
  }
}



// WEBPACK FOOTER //
// ./src/client/store/MessageStore.js